import React, { useRef, useState } from 'react';
import {
  XMarkIcon,
  CameraIcon,
  DocumentArrowUpIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import { ExpenseKind, ExpenseExtractSuggestions, ProfessionalExpense } from '../types';
import { ImportReviewPanel } from './ImportReviewPanel';
import {
  baseFromTotalWithVat,
  computeTicketAmounts,
  defaultVatRateFromCategory,
  roundMoney
} from '../utils/fiscalUtils';

const KIND_OPTIONS: { value: ExpenseKind; title: string; desc: string }[] = [
  {
    value: 'suplido',
    title: 'Suplido',
    desc: 'Gasto real del cliente. No entra en base imponible; se repercutirá como suplido en factura.'
  },
  {
    value: 'no_suplido',
    title: 'No suplido (repercutible)',
    desc: 'Gasto que facturará al cliente con IVA/IRPF (entradas, dietas, transporte…).'
  },
  {
    value: 'profesional_deducible',
    title: 'Gasto profesional deducible',
    desc: 'Gasto de su actividad deducible en IRPF/IVA. No se repercutirá en factura.'
  }
];

const CATEGORIES = [
  'Transporte', 'Taxi', 'Gasolina', 'Peaje', 'Dietas', 'Hotel', 'Material', 'Software',
  'Seguros', 'Formacion', 'Telefonia', 'Suministros', 'Otros'
];



function syncTicketBaseFromTotal(
  form: ExpenseFormState,
  setForm: React.Dispatch<React.SetStateAction<ExpenseFormState>>,
  categoria: string,
  totalStr: string,
  ivaStr: string
) {
  const total = parseFloat(String(totalStr).replace(',', '.')) || 0;
  const ivaRate = Number(ivaStr) || defaultVatRateFromCategory(categoria);
  if (total <= 0) {
    setForm((f) => ({ ...f, baseImponible: '' }));
    return;
  }
  const base = baseFromTotalWithVat(total, ivaRate);
  setForm((f) => ({ ...f, baseImponible: String(base), ivaPorcentaje: String(ivaRate) }));
}

function NoSuplidoFiscalFields({
  form,
  setForm
}: {
  form: ExpenseFormState;
  setForm: React.Dispatch<React.SetStateAction<ExpenseFormState>>;
}) {
  const total = parseFloat(String(form.total).replace(',', '.')) || 0;
  const ivaRate = Number(form.ivaPorcentaje) || defaultVatRateFromCategory(form.categoria);
  const ticket = total > 0 ? computeTicketAmounts(total, ivaRate) : null;

  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 space-y-2">
      <p className="text-xs font-medium text-blue-900">Ticket con IVA del proveedor (no suplido)</p>
      <div>
        <label className="block text-xs text-gray-600 mb-1">Importe ticket (total con IVA)</label>
        <input
          type="number"
          step="0.01"
          value={form.total}
          onChange={(e) => {
            const v = e.target.value;
            setForm((f) => ({ ...f, total: v }));
            syncTicketBaseFromTotal(form, setForm, form.categoria, v, form.ivaPorcentaje);
          }}
          className="w-full border rounded-md px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">Tipo IVA línea (proveedor / repercutido)</label>
        <select
          value={ivaRate}
          onChange={(e) => {
            const v = e.target.value;
            setForm((f) => ({ ...f, ivaPorcentaje: v }));
            syncTicketBaseFromTotal(form, setForm, form.categoria, form.total, v);
          }}
          className="w-full border rounded-md px-2 py-1.5 text-sm"
        >
          <option value={10}>10% (transporte)</option>
          <option value={21}>21% (general)</option>
        </select>
      </div>
      {ticket && (
        <div className="text-xs text-gray-700 space-y-0.5 pt-1 border-t border-blue-100">
          <div className="flex justify-between">
            <span>Base imponible (para factura cliente)</span>
            <span>{ticket.ticket_base_amount.toFixed(2)} €</span>
          </div>
          <div className="flex justify-between">
            <span>IVA ticket ({ticket.ticket_vat_rate}%)</span>
            <span>{ticket.ticket_vat_amount.toFixed(2)} €</span>
          </div>
          <div className="flex justify-between font-medium">
            <span>Total ticket</span>
            <span>{ticket.ticket_total.toFixed(2)} €</span>
          </div>
          <p className="text-[11px] text-gray-500 pt-1">
            Fórmula: base = total ÷ (1 + IVA%/100). En la factura al cliente se repercutirá la base con el mismo tipo de IVA.
          </p>
        </div>
      )}
    </div>
  );
}

type EntryTab = 'photo' | 'file' | 'manual';
type Step = 'kind' | 'capture' | 'review';

type ExpenseFormState = {
  proveedor: string;
  cifProveedor: string;
  numeroFactura: string;
  fecha: string;
  categoria: string;
  descripcion: string;
  baseImponible: string;
  ivaPorcentaje: string;
  total: string;
  formaPago: string;
};

interface AddExpenseModalProps {
  onClose: () => void;
  onSaved: (expense: ProfessionalExpense) => void;
  initialKind?: ExpenseKind;
  inline?: boolean;
}

export const AddExpenseModal: React.FC<AddExpenseModalProps> = ({
  onClose,
  onSaved,
  initialKind,
  inline = false
}) => {
  const [step, setStep] = useState<Step>(initialKind ? 'capture' : 'kind');
  const [expenseKind, setExpenseKind] = useState<ExpenseKind | null>(initialKind || null);
  const [entryTab, setEntryTab] = useState<EntryTab>('photo');
  const [file, setFile] = useState<File | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<ExpenseExtractSuggestions>({});
  const [ocrJson, setOcrJson] = useState<Record<string, unknown> | null>(null);

  const [form, setForm] = useState<ExpenseFormState>({
    proveedor: '',
    cifProveedor: '',
    numeroFactura: '',
    fecha: new Date().toISOString().split('T')[0],
    categoria: 'Otros',
    descripcion: '',
    baseImponible: '',
    ivaPorcentaje: '21',
    total: '',
    formaPago: ''
  });

  const photoRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const applySuggestions = (s: ExpenseExtractSuggestions) => {
    setForm((prev) => ({
      ...prev,
      proveedor: s.proveedor || prev.proveedor,
      cifProveedor: s.cifProveedor || prev.cifProveedor,
      numeroFactura: s.numeroFactura || prev.numeroFactura,
      fecha: s.fecha || prev.fecha,
      categoria: s.categoria && CATEGORIES.includes(s.categoria) ? s.categoria : prev.categoria,
      descripcion: s.descripcion || prev.descripcion,
      ivaPorcentaje:
        s.ivaPorcentaje != null
          ? String(s.ivaPorcentaje)
          : String(defaultVatRateFromCategory(s.categoria || prev.categoria)),
      total: s.total != null ? String(s.total) : prev.total,
      baseImponible:
        s.baseImponible != null
          ? String(s.baseImponible)
          : s.total != null
            ? String(
                baseFromTotalWithVat(
                  Number(s.total),
                  Number(s.ivaPorcentaje ?? defaultVatRateFromCategory(s.categoria || prev.categoria))
                )
              )
            : prev.baseImponible,
      formaPago: s.formaPago || prev.formaPago
    }));
  };

  const handleFilePick = async (f: File | null) => {
    if (!f) return;
    setFile(f);
    if (entryTab === 'manual') {
      setStep('review');
      return;
    }
    setExtracting(true);
    setStep('review');
    try {
      const res = await apiService.extractExpenseTicket(f, expenseKind || undefined);
      const suggestions = res.data?.suggestions;
      if (suggestions) {
        setSuggestions(suggestions);
        applySuggestions(suggestions);
        setOcrJson(res.data?.ocrJson || null);
      } else {
        setSuggestions({
          message: res.message || res.error || 'No se pudo extraer datos del ticket',
          revisionRequerida: true,
          ocrConfidence: 20
        });
      }
    } catch (err) {
      setSuggestions({
        message: err instanceof Error ? err.message : 'Error al analizar el ticket',
        revisionRequerida: true,
        ocrConfidence: 0
      });
    } finally {
      setExtracting(false);
    }
  };

  const saveExpense = async () => {
    if (!expenseKind) {
      alert('Seleccione el tipo de gasto');
      return;
    }
    if (!form.proveedor.trim() || !form.fecha) {
      alert('Proveedor y fecha son obligatorios');
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append('expenseKind', expenseKind);
    fd.append('proveedor', form.proveedor);
    fd.append('fecha', form.fecha);
    fd.append('categoria', form.categoria);
    if (form.cifProveedor) fd.append('cifProveedor', form.cifProveedor);
    if (form.numeroFactura) fd.append('numeroFactura', form.numeroFactura);
    if (form.descripcion) fd.append('descripcion', form.descripcion);
    const isNoSuplido = expenseKind === 'no_suplido';
    let baseToSave = form.baseImponible;
    let ivaToSave = form.ivaPorcentaje;
    let totalToSave = form.total;
    if (isNoSuplido && form.total) {
      const ivaRate = Number(form.ivaPorcentaje) || defaultVatRateFromCategory(form.categoria);
      const ticket = computeTicketAmounts(parseFloat(String(form.total).replace(',', '.')) || 0, ivaRate);
      baseToSave = String(ticket.ticket_base_amount);
      ivaToSave = String(ticket.ticket_vat_rate);
      totalToSave = String(ticket.ticket_total);
    }
    if (baseToSave) fd.append('baseImponible', baseToSave);
    if (ivaToSave) fd.append('ivaPorcentaje', ivaToSave);
    if (totalToSave) fd.append('total', totalToSave);

    if (form.formaPago) fd.append('formaPago', form.formaPago);
    if (suggestions.ocrConfidence != null) fd.append('ocrConfidence', String(suggestions.ocrConfidence));
    if (ocrJson) fd.append('ocrJson', JSON.stringify(ocrJson));
    if (file) fd.append('file', file);

    const res = await apiService.createProfessionalExpense(fd);
    setSaving(false);
    if (res.error || !res.data?.expense) {
      alert(res.message || res.error || 'Error al guardar');
      return;
    }
    onSaved(res.data.expense);
    onClose();
  };

  const shellClass = inline
    ? 'bg-white rounded-lg border p-4'
    : 'fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4';

  const panelClass = inline
    ? 'w-full'
    : 'bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col';

  return (
    <div className={shellClass}>
      <div className={panelClass}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Añadir gasto</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0 flex-col lg:flex-row">
          <div className="flex-1 p-4 overflow-y-auto">
            {step === 'kind' && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-4">
                  Paso 1 — Clasifique el gasto antes de capturar o introducir datos.
                </p>
                {KIND_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setExpenseKind(opt.value);
                      setStep('capture');
                    }}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                      expenseKind === opt.value
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{opt.title}</div>
                    <div className="text-sm text-gray-600 mt-1">{opt.desc}</div>
                  </button>
                ))}
              </div>
            )}

            {step === 'capture' && (
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Paso 2 — Tipo:{' '}
                  <strong>{KIND_OPTIONS.find((k) => k.value === expenseKind)?.title}</strong>
                </p>
                <div className="flex gap-2 mb-4">
                  {(['photo', 'file', 'manual'] as EntryTab[]).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setEntryTab(tab)}
                      className={`flex items-center gap-1 px-3 py-2 text-sm rounded-md ${
                        entryTab === tab ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {tab === 'photo' && <CameraIcon className="h-4 w-4" />}
                      {tab === 'file' && <DocumentArrowUpIcon className="h-4 w-4" />}
                      {tab === 'manual' && <PencilSquareIcon className="h-4 w-4" />}
                      {tab === 'photo' ? 'Foto' : tab === 'file' ? 'Archivo' : 'Manual'}
                    </button>
                  ))}
                </div>

                {entryTab === 'photo' && (
                  <div>
                    <input
                      ref={photoRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handleFilePick(e.target.files?.[0] || null)}
                    />
                    <button
                      type="button"
                      onClick={() => photoRef.current?.click()}
                      disabled={extracting}
                      className="w-full py-12 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 text-gray-600"
                    >
                      {extracting ? 'Analizando ticket…' : 'Hacer foto del ticket'}
                    </button>
                  </div>
                )}

                {entryTab === 'file' && (
                  <div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => handleFilePick(e.target.files?.[0] || null)}
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={extracting}
                      className="w-full py-12 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 text-gray-600"
                    >
                      {extracting ? 'Analizando ticket…' : 'Subir PDF o imagen'}
                    </button>
                  </div>
                )}

                {entryTab === 'manual' && (
                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                      setStep('review');
                    }}
                    className="w-full py-8 bg-gray-50 border rounded-lg text-gray-700 hover:bg-gray-100"
                  >
                    Continuar al formulario manual
                  </button>
                )}

                <button
                  type="button"
                  className="mt-4 text-sm text-blue-600"
                  onClick={() => setStep('kind')}
                >
                  ← Cambiar tipo de gasto
                </button>
              </div>
            )}

            {step === 'review' && extracting && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-600">
                <div className="h-10 w-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4" />
                <p className="text-sm font-medium">Analizando ticket…</p>
                <p className="text-xs text-gray-500 mt-1">Extrayendo proveedor, fecha, importes e IVA</p>
              </div>
            )}

            {step === 'review' && !extracting && (
              <div className="lg:hidden space-y-3">
                <p className="text-sm font-medium text-gray-700">Datos del gasto</p>
                {renderFormFields(form, setForm, CATEGORIES, expenseKind)}
                <div className="flex gap-2">
                  <button type="button" onClick={() => setStep('capture')} className="flex-1 border rounded-md py-2 text-sm">
                    Volver
                  </button>
                  <button
                    type="button"
                    onClick={saveExpense}
                    disabled={saving}
                    className="flex-1 bg-blue-600 text-white rounded-md py-2 text-sm"
                  >
                    {saving ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {step === 'review' && (
            <div className="hidden lg:block">
              <ImportReviewPanel
                suggestions={suggestions}
                confidence={suggestions.ocrConfidence}
                fields={buildReviewFields(form, setForm, CATEGORIES, expenseKind)}
                onConfirm={saveExpense}
                onCancel={() => setStep('capture')}
                loading={saving}
              />
              {expenseKind === 'no_suplido' && (
                <div className="p-4 border-l">
                  <NoSuplidoFiscalFields form={form} setForm={setForm} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function buildReviewFields(
  form: ExpenseFormState,
  setForm: React.Dispatch<React.SetStateAction<ExpenseFormState>>,
  categories: string[],
  expenseKind: ExpenseKind | null
) {
  const set = (key: keyof ExpenseFormState) => (val: string) =>
    setForm((f) => ({ ...f, [key]: val }));
  const onCategoria = (val: string) => {
    const iva = String(defaultVatRateFromCategory(val));
    setForm((f) => {
      const next = { ...f, categoria: val, ivaPorcentaje: expenseKind === 'no_suplido' ? iva : f.ivaPorcentaje };
      if (expenseKind === 'no_suplido' && f.total) {
        next.baseImponible = String(baseFromTotalWithVat(parseFloat(f.total.replace(',', '.')) || 0, Number(iva)));
      }
      return next;
    });
  };
  const baseFields = [
    { key: 'proveedor', label: 'Proveedor *', value: form.proveedor, onChange: set('proveedor') },
    { key: 'fecha', label: 'Fecha *', value: form.fecha, onChange: set('fecha'), type: 'date' as const },
    { key: 'cifProveedor', label: 'CIF proveedor', value: form.cifProveedor, onChange: set('cifProveedor') },
    { key: 'numeroFactura', label: 'Nº factura/ticket', value: form.numeroFactura, onChange: set('numeroFactura') },
    {
      key: 'categoria',
      label: 'Categoría',
      value: form.categoria,
      onChange: onCategoria,
      options: categories
    },
    { key: 'descripcion', label: 'Descripción', value: form.descripcion, onChange: set('descripcion') },
    { key: 'formaPago', label: 'Forma de pago', value: form.formaPago, onChange: set('formaPago') }
  ];
  if (expenseKind === 'no_suplido') return baseFields;
  return [
    ...baseFields.slice(0, 6),
    { key: 'baseImponible', label: 'Base imponible', value: form.baseImponible, onChange: set('baseImponible'), type: 'number' as const },
    { key: 'ivaPorcentaje', label: 'IVA %', value: form.ivaPorcentaje, onChange: set('ivaPorcentaje'), type: 'number' as const },
    { key: 'total', label: 'Total', value: form.total, onChange: set('total'), type: 'number' as const },
    baseFields[6]
  ];
}

function renderFormFields(
  form: ExpenseFormState,
  setForm: React.Dispatch<React.SetStateAction<ExpenseFormState>>,
  categories: string[],
  expenseKind: ExpenseKind | null
) {
  const fields = buildReviewFields(form, setForm, categories, expenseKind);
  return (
    <>
      {fields.map((f) => (
        <div key={String(f.key)}>
          <label className="block text-xs text-gray-600 mb-1">{f.label}</label>
          {'options' in f && f.options ? (
            <select
              value={f.value ?? ''}
              onChange={(e) => f.onChange(e.target.value)}
              className="w-full border rounded-md px-2 py-1.5 text-sm"
            >
              {f.options.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={f.type || 'text'}
              value={f.value ?? ''}
              onChange={(e) => f.onChange(e.target.value)}
              className="w-full border rounded-md px-2 py-1.5 text-sm"
            />
          )}
        </div>
      ))}
      {expenseKind === 'no_suplido' && <NoSuplidoFiscalFields form={form} setForm={setForm} />}
    </>
  );
}
